<template>
  <b-modal
    id="service-system-replace-operator-modal"
    cancel-variant="outline-secondary"
    :cancel-title="$t('button.close')"
    centered
    no-close-on-backdrop
    :title="$t('general.modal_title_replace_operator')"
    :loading="loading"
    @ok="handleOk"
    @cancel="handleClose"
  >
    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        class="btn"
        @click="handleClose"
      >
        {{ $t('button.close') }}
      </b-button>
      <n-button-loading
        :loading="loading"
        :disabled="!data.assignedOperatorId"
        class="btn"
        variant="primary"
        @submit="handleOk"
      >
        {{ $t('button.confirm') }}
      </n-button-loading>
    </template>
    <validation-observer
      ref="replaceOperatorForm"
      #default="{ invalid }"
    >
      <n-form-confirmation
        ref="formConfirmation"
        key="replaceOperatorForm"
        :message="$t('general.are_you_sure_you_want_to_confirm_this_operation')"
        :confirm-button-text="`button.confirm`"
        :cancel-button-text="`button.no`"
        :form="$refs.replaceOperatorForm"
        :disabled="invalid"
        @submit="submit"
      >
        <!-- <div class="service-info mb-2">
          <div class="d-flex">
            <label class="mb-0 font-weight-bold">{{
              $t('field.serviceName')
            }}</label>
            <p class="mb-0">
              {{ serviceName }}
            </p>
          </div>
          <div class="d-flex">
            <label class="mb-0 font-weight-bold">{{
              $t('field.currentOperator')
            }}</label>
            <p class="mb-0">
              {{ operatorName }}
            </p>
          </div>
        </div> -->
        <!-- <div class="service-action"> -->
        <label class="d-block">
          {{ $t('general.new_operator') }}
        </label>
        <n-async-single-select
          key="merchantUserId"
          v-model="data.assignedOperatorId"
          repository="operatorForService"
          selection-key="id"
          selection-label="name"
          rules="required"
          :placeholder="$t('general.choose_operator')"
          :readonly="!canEditOperator"
          :query="{
            isResetService: true,
            serviceType: data.serviceType,
            serviceCategory: data.serviceCategory,
            projectId: data.projectId
          }"
          always-fresh
          :must-excluded-ids="mustExcludedIds"
          :get-option-label="getLabel"
          :selectable="(option) => !option.isDisabledBreakTime"
          @optionChange="onChangeOperator"
        >
          <template #option="item">
            {{ item.name }}
            <div
              v-if="item.totalServiceCap"
              class="float-right mr-2"
              style="display: flex; align-items: center !important;"
            >
              <div
                v-if="item.isDisabledBreakTime"
                class="mr-1"
                style="margin-bottom: 2px;"
              >
                <feather-icon
                  v-b-tooltip.hover.left
                  icon="CoffeeIcon"
                  class="text-danger"
                  :title="$t('general.on_break_time')"
                />
              </div>
              <span>{{ `${item.totalServiceWork}/${item.totalServiceCap}` }}</span>
            </div>
          </template>
        </n-async-single-select>
        <!-- </div> -->
      </n-form-confirmation>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import NFormConfirmation from '@/components/NFormConfirmation.vue'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import NButtonLoading from '@/components/NButtonLoading.vue'
import ToastificationAvatar from '@core/components/toastification/ToastificationAvatar.vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

const ServiceSystemRepository = Repository.get('serviceSystem')

export default {
  name: 'ReplaceOperatorModal',
  components: {
    BModal,
    BButton,
    NFormConfirmation,
    NAsyncSingleSelect,
    NButtonLoading,
    FeatherIcon,
  },
  props: {
    mustExcludedIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      data: {
        serviceSystemId: '',
        assignedOperatorId: '',
        currentOperatorId: '',
        departmentId: '',
        serviceType: '',
        serviceCategory: '',
        projectId: '',
      },
      operatorName: '',
      serviceName: '',
    }
  },
  computed: {
    canEditOperator() {
      return this.$can('manage', 'manage-modify-service')
    },
  },
  methods: {
    getLabel(option) {
      return option.name || ''
    },
    handleClose() {
      this.hide()
      this.data = {
        serviceSystemId: '',
        assignedOperatorId: '',
        currentOperatorId: '',
        departmentId: '',
        serviceType: '',
        serviceCategory: '',
        projectId: '',
      }
    },
    show(serviceSystem, operatorId) {
      this.data = {
        serviceSystemId: serviceSystem.id,
        currentOperatorId: serviceSystem.assignedOperatorId,
        departmentId: serviceSystem.departmentId,
        serviceType: serviceSystem.serviceType?.code,
        serviceCategory: serviceSystem.serviceCategory?.code,
        projectId: serviceSystem.projectId,
        operatorId,
      }
      this.operatorName = serviceSystem.assignedOperator
      this.serviceName = serviceSystem.shortenName
      this.$bvModal.show('service-system-replace-operator-modal')
    },
    hide() {
      this.$bvModal.hide('service-system-replace-operator-modal')
    },
    onChangeOperator(data) {
      this.newOperatorName = data?.name
    },
    handleOk(bvModal) {
      if (bvModal) bvModal.preventDefault()
      if (this.loading) return

      if (this.$refs.replaceOperatorForm?.validate) {
        this.$refs.replaceOperatorForm.validate().then(success => {
          if (success) {
            this.$refs.formConfirmation.confirm()
          }
        })
      } else {
        this.submit()
      }
    },
    async submit() {
      this.loading = true
      try {
        const response = await ServiceSystemRepository.replaceHelper(this.data.operatorId, this.data.assignedOperatorId)

        this.$toast({
          component: ToastificationAvatar,
          position: 'top-right',
          timeout: 10000,
          props: {
            title: this.$t('alert.service'),
            icon: 'CastIcon',
            variant: 'success',
            text: this.$t('general.operatorReplacedSuccessfully'),
          },
        })

        this.hide()
        this.$emit('success', response)
      } catch (error) {
        if (error.response?.status === 422) {
          this.$refs.replaceOperatorForm.setErrors(error.response?.data?.validate)
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.service-info {
  background-color: #f8f8f8;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;

  label {
    color: #6e6b7b;
    width: 160px;
    font-size: 1rem;
    margin-bottom: 0.75rem !important;
  }

  p {
    color: #5e5873;
    font-size: 1rem;
  }
}

.service-action {
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #eaeaea;
  margin-bottom: 0.5rem;

  label {
    color: #6e6b7b;
    font-size: 1rem;
    font-weight: 500;
  }
}
</style>
